import { useMemo } from "react"
import { observer } from "mobx-react-lite"

import { InnerCard, InnerCardWarning, ShowWhenTrue } from "@/components/index"
import { DollarIcon, WalletIcon } from "@/domains/icons"
import { cn, dollars } from "@/domains/library"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { ITokenToggle, TokenToggleButtonsSlider } from "@/domains/tokens/components/token-toggle-buttons-slider"
import { INSUFFICIENT_BALANCE_ERROR } from "@/features/liquidity/constants"

export const MinimalPoolInputCard = observer(
  ({
    amount,
    balance,
    minimalPool,
    onAmountChange,
    balanceSlot,
  }: {
    amount: string
    balance: bigint
    minimalPool: MinimalPoolStore | null | undefined
    balanceSlot: React.ReactNode
    onAmountChange: (newAmount: string) => void
  }) => {
    if (!minimalPool) {
      return null
    }
    const dollarValue = minimalPool.calculateValueUSD(Number(amount))
    const amountWei = minimalPool.parseUnits(amount)

    // Compute slider value on the fly from the current amount
    // This ensures the value is updated when the amount changes
    const sliderValue = useMemo(() => {
      return balance > 0
        ? Math.min(100, Math.max(0, (Number(amount) / Number(minimalPool.formatUnits(balance))) * 100))
        : 0
    }, [amount, balance, minimalPool])

    const error = amountWei > balance ? INSUFFICIENT_BALANCE_ERROR : null
    const hasInsufficientBalance = error === INSUFFICIENT_BALANCE_ERROR

    const buttonsToken: ITokenPercentageButtons = {
      symbol: minimalPool.displayName ?? "unknown",
      imageURL: minimalPool.token0.imageURL ?? "",
      decimals: minimalPool.decimals ?? 18,
      calculateValue: (percentage: number): string => {
        return minimalPool.calculateValuePercentage(balance, percentage) ?? "0"
      },
    }

    const token: ITokenToggle = {
      balance,
      formatUnits: minimalPool.formatUnits,
    }

    return (
      <InnerCard hasWarning={hasInsufficientBalance}>
        <div className="flex flex-row items-center justify-between">
          <TokenInputValue
            value={amount}
            token={minimalPool}
            onValueChange={onAmountChange}
            className={cn("h-fit p-0")}
            placeholder="0"
          />
        </div>

        <div className="flex w-full flex-row items-center justify-between text-xs">
          <ShowWhenTrue when={hasInsufficientBalance === false}>
            <div className="text-t200 flex items-center gap-1 text-sm">
              <DollarIcon size={14} />
              <p>~{dollars(dollarValue)}</p>
            </div>
          </ShowWhenTrue>
          <ShowWhenTrue when={hasInsufficientBalance}>
            <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
          </ShowWhenTrue>
          {balanceSlot}
        </div>

        <TokenToggleButtonsSlider
          buttonsToken={buttonsToken}
          disabled={{ amount: false, percentageButtons: false }}
          sliderValue={sliderValue}
          token={token}
          onSetSliderValue={() => {}}
          onTokenAmountChange={onAmountChange}
        />
      </InnerCard>
    )
  },
)
